<template>
  <div class="py-4 container-fluid">
    <!-- Statistics Cards -->
    <div class="row mb-4">
      <div class="col-xl-4 col-sm-6 mb-xl-0 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-capitalize font-weight-bold">
                    總訂單數
                  </p>
                  <h5 class="font-weight-bolder mb-0">
                    {{ stats.total_orders }}
                  </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div
                  class="icon icon-shape bg-gradient-primary shadow text-center border-radius-md"
                >
                  <i
                    class="ni ni-cart text-lg opacity-10"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-4 col-sm-6 mb-xl-0 mb-4">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-capitalize font-weight-bold">
                    總營收
                  </p>
                  <h5 class="font-weight-bolder mb-0">
                    NT$ {{ stats.total_revenue }}
                  </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div
                  class="icon icon-shape bg-gradient-success shadow text-center border-radius-md"
                >
                  <i
                    class="ni ni-money-coins text-lg opacity-10"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-xl-4 col-sm-6">
        <div class="card">
          <div class="card-body p-3">
            <div class="row">
              <div class="col-8">
                <div class="numbers">
                  <p class="text-sm mb-0 text-capitalize font-weight-bold">
                    總佣金
                  </p>
                  <h5 class="font-weight-bolder mb-0">
                    NT$ {{ stats.total_commission }}
                  </h5>
                </div>
              </div>
              <div class="col-4 text-end">
                <div
                  class="icon icon-shape bg-gradient-warning shadow text-center border-radius-md"
                >
                  <i
                    class="ni ni-briefcase-24 text-lg opacity-10"
                    aria-hidden="true"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Chart -->
    <div class="row mb-4" v-if="chartData && chartData.labels.length > 0">
      <div class="col-12">
        <div class="card z-index-2">
          <gradient-line-chart
            id="chart-line"
            title="營收趨勢"
            description="顯示所選期間內的每日營收趨勢"
            :chart="chartData"
          />
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <div class="card mb-4">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center">
              <h6>營收報告</h6>
              <button
                class="btn btn-outline-success btn-sm mb-0"
                @click="exportExcel"
              >
                <i class="fas fa-file-excel me-1"></i> 匯出 Excel
              </button>
            </div>
            <!-- Filters -->
            <div class="row mt-3 mb-3">
              <div class="col-md-4">
                <label>開始日期</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="filters.start_date"
                />
              </div>
              <div class="col-md-4">
                <label>結束日期</label>
                <input
                  type="date"
                  class="form-control"
                  v-model="filters.end_date"
                />
              </div>
              <div class="col-md-4 d-flex align-items-end">
                <soft-button
                  color="primary"
                  size="sm"
                  class="me-2"
                  @click="fetchRevenue"
                  >搜尋</soft-button
                >
                <soft-button color="secondary" size="sm" @click="resetFilters"
                  >重置</soft-button
                >
              </div>
            </div>
          </div>
          <div class="card-body px-0 pt-0 pb-2">
            <div class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      訂單編號
                    </th>
                    <th
                      class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2"
                    >
                      日期
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      總金額
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      平台費用
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      調整
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      實際撥款
                    </th>
                    <th
                      class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7"
                    >
                      狀態
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="7" class="text-center py-4">
                      <div class="spinner-border text-success" role="status">
                        <span class="visually-hidden">Loading...</span>
                      </div>
                      <p class="mt-2 text-sm text-secondary">載入中...</p>
                    </td>
                  </tr>
                  <template v-else>
                    <tr v-for="item in revenueItems" :key="item.id">
                      <td>
                        <div class="d-flex px-2 py-1">
                          <div
                            class="d-flex flex-column justify-content-center"
                          >
                            <h6 class="mb-0 text-sm">
                              #{{ item.order_number }}
                            </h6>
                          </div>
                        </div>
                      </td>
                      <td>
                        <p class="text-xs font-weight-bold mb-0">
                          {{ new Date(item.created_at).toLocaleDateString() }}
                        </p>
                      </td>
                      <td class="align-middle text-center text-sm">
                        <span class="text-secondary text-xs font-weight-bold"
                          >NT$ {{ item.total_amount }}</span
                        >
                      </td>
                      <td class="align-middle text-center text-sm">
                        <span class="text-secondary text-xs font-weight-bold"
                          >NT$ {{ item.platform_fee }}</span
                        >
                      </td>
                      <td class="align-middle text-center text-sm">
                        <span
                          :class="
                            item.special_adjustment !== 0
                              ? 'text-danger font-weight-bold'
                              : 'text-secondary text-xs'
                          "
                        >
                          {{ item.special_adjustment || 0 }}
                        </span>
                      </td>
                      <td class="align-middle text-center text-sm">
                        <span class="text-success text-xs font-weight-bold"
                          >NT$ {{ item.actual_payout }}</span
                        >
                      </td>
                      <td class="align-middle text-center text-sm">
                        <soft-badge
                          :color="
                            item.status === 'completed'
                              ? 'success'
                              : 'secondary'
                          "
                          variant="gradient"
                          size="sm"
                        >
                          {{ translateStatus(item.status) }}
                        </soft-badge>
                      </td>
                    </tr>
                    <tr v-if="revenueItems.length === 0">
                      <td colspan="7" class="text-center py-4 text-secondary">
                        <i class="ni ni-money-coins text-lg mb-2 d-block"></i>
                        找不到營收記錄
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
            <!-- Pagination -->
            <div
              class="d-flex justify-content-center mt-3"
              v-if="pagination.total > 0"
            >
              <soft-pagination color="success">
                <soft-pagination-item
                  prev
                  @click="changePage(pagination.current_page - 1)"
                  :disabled="pagination.current_page === 1"
                />
                <soft-pagination-item
                  label
                  v-for="page in pagination.last_page"
                  :key="page"
                  :active="page === pagination.current_page"
                  @click="changePage(page)"
                  >{{ page }}</soft-pagination-item
                >
                <soft-pagination-item
                  next
                  @click="changePage(pagination.current_page + 1)"
                  :disabled="pagination.current_page === pagination.last_page"
                />
              </soft-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SoftBadge from "@/components/SoftBadge.vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftPagination from "@/components/SoftPagination.vue";
import SoftPaginationItem from "@/components/SoftPaginationItem.vue";
import GradientLineChart from "@/examples/Charts/GradientLineChart.vue";
import request from "@/utils/request";
import Swal from "sweetalert2";

export default {
  name: "Revenue",
  components: {
    SoftBadge,
    SoftButton,
    SoftPagination,
    SoftPaginationItem,
    GradientLineChart,
  },
  data() {
    return {
      revenueItems: [],
      loading: false,
      stats: {
        total_orders: 0,
        total_revenue: 0,
        total_commission: 0,
      },
      chartData: {
        labels: [],
        datasets: [],
      },
      filters: {
        start_date: "",
        end_date: "",
      },
      pagination: {
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10,
      },
    };
  },
  methods: {
    translateStatus(status) {
      const statusMap = {
        completed: "已完成",
        pending: "待處理",
        processing: "處理中",
        cancelled: "已取消",
      };
      return statusMap[status] || status;
    },
    async fetchRevenue(page = 1) {
      this.loading = true;
      try {
        const params = {
          page,
          ...this.filters,
        };
        Object.keys(params).forEach(
          (key) =>
            (params[key] === "" || params[key] === null) && delete params[key]
        );

        const response = await request.get("/api/admin/revenue", { params });
        const result = response.data.data || {};
        this.revenueItems = result.items || [];
        // Helper to ensure stats object has all keys to prevent render errors
        const defaultStats = {
          total_orders: 0,
          total_revenue: 0,
          total_commission: 0,
        };
        this.stats = result.stats
          ? { ...defaultStats, ...result.stats }
          : defaultStats;

        // Process Chart Data
        if (result.chart_data && Array.isArray(result.chart_data)) {
          const labels = result.chart_data.map((item) => item.date);
          const revenueData = result.chart_data.map((item) => item.revenue);
          this.chartData = {
            labels: labels,
            datasets: [
              {
                label: "營收",
                data: revenueData,
              },
            ],
          };
        } else {
          this.chartData = { labels: [], datasets: [] };
        }

        this.pagination = response.data.meta || {
          current_page: 1,
          last_page: 1,
          total: 0,
          per_page: 10,
        };
      } catch (error) {
        console.error("Failed to fetch revenue", error);
        this.$swal.fire("錯誤", "載入數據失敗", "error");
      } finally {
        this.loading = false;
      }
    },
    resetFilters() {
      this.filters = {
        start_date: "",
        end_date: "",
      };
      this.fetchRevenue(1);
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.fetchRevenue(page);
      }
    },
    async exportExcel() {
      try {
        const params = { ...this.filters, export: "excel" };
        Object.keys(params).forEach(
          (key) =>
            (params[key] === "" || params[key] === null) && delete params[key]
        );

        // In a real app, this would trigger a file download
        // const response = await request.get('/api/admin/revenue/export', { params, responseType: 'blob' });
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement('a');
        // link.href = url;
        // link.setAttribute('download', 'revenue_report.xlsx');
        // document.body.appendChild(link);
        // link.click();

        Swal.fire("成功", "匯出功能將在此處下載 Excel 文件。", "success");
      } catch (error) {
        Swal.fire("錯誤", "匯出失敗", "error");
      }
    },
  },
  created() {
    this.fetchRevenue();
  },
};
</script>
